import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { whatsappApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';
import { 
  WhatsappLogo, 
  ChatCircle, 
  QrCode, 
  Plugs, 
  PaperPlaneTilt, 
  Robot, 
  FileText, 
  ListDashes, 
  ArrowUpRight, 
  ArrowDownLeft, 
  ArrowsCounterClockwise,
  CheckCircle,
  Users,
  Megaphone,
  Tray
} from '@phosphor-icons/react';

// Modular Subcomponents (moved from Config page)
import { WhatsAppChatbotForm } from '@/components/admin/WhatsAppChatbotForm';
import { WhatsAppTemplatesForm } from '@/components/admin/WhatsAppTemplatesForm';
import { WhatsAppInbox } from '@/components/admin/WhatsAppInbox';
import { WhatsAppBroadcast } from '@/components/admin/WhatsAppBroadcast';

export const WhatsAppHub = () => {
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const setTenantConfig = useAppStore((state) => state.setTenantConfig);

  const [activeTab, setActiveTab] = useState('gateway');

  // WhatsApp Gateway State
  const [waStatus, setWaStatus] = useState('disconnected');
  const [waLoading, setWaLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Hello! This is a test message from KWICC WhatsApp Service.');
  const [sendingTest, setSendingTest] = useState(false);

  // Conversation Simulator State
  const [simPhone, setSimPhone] = useState('919999999999');
  const [simInput, setSimInput] = useState('');
  const [simMessages, setSimMessages] = useState([]); // { from: 'user'|'bot', text }
  const [simSending, setSimSending] = useState(false);

  // Activity Logs State
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logSearch, setLogSearch] = useState('');
  const [logTypeFilter, setLogTypeFilter] = useState('all');
  const [autoRefreshLogs, setAutoRefreshLogs] = useState(true);

  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';

  // --- Activity Logs Handlers ---
  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const { data } = await whatsappApi.getLogs();
      setLogs(data || []);
    } catch (error) {
      console.error('Failed to fetch WhatsApp logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab]);

  useEffect(() => {
    let interval;
    if (activeTab === 'logs' && autoRefreshLogs) {
      interval = setInterval(() => {
        fetchLogs();
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [activeTab, autoRefreshLogs]);

  // --- Conversation Simulator Handlers ---
  const sendSimMessage = async () => {
    const text = simInput.trim();
    if (!text || simSending) return;
    if (!simPhone.trim()) {
      toast.error('Enter a test phone number first.');
      return;
    }
    setSimMessages((prev) => [...prev, { from: 'user', text }]);
    setSimInput('');
    setSimSending(true);
    try {
      const { data } = await whatsappApi.simulate(simPhone.trim(), text);
      const replies = data?.replies || [];
      if (replies.length === 0) {
        setSimMessages((prev) => [...prev, { from: 'bot', text: '(no reply — bot ignored this message)', muted: true }]);
      } else {
        setSimMessages((prev) => [...prev, ...replies.map((r) => ({ from: 'bot', text: r }))]);
      }
    } catch (error) {
      toast.error('Simulation failed. Check that the bot is enabled.');
      setSimMessages((prev) => [...prev, { from: 'bot', text: 'Error: simulation request failed.', muted: true }]);
    } finally {
      setSimSending(false);
    }
  };

  const resetSimulator = () => setSimMessages([]);

  // --- Gateway Handlers ---
  const fetchWaStatus = async () => {
    try {
      const { data } = await whatsappApi.getStatus();
      setWaStatus(data.state || 'disconnected');
    } catch (error) {
      console.error('Failed to fetch WhatsApp status:', error);
    } finally {
      setWaLoading(false);
    }
  };

  useEffect(() => {
    fetchWaStatus();
  }, []);

  // Poll connection state when QR code is active
  useEffect(() => {
    let interval;
    if (qrCode && waStatus !== 'open' && waStatus !== 'connected') {
      interval = setInterval(async () => {
        try {
          const { data } = await whatsappApi.getStatus();
          const currentState = data.state || 'disconnected';
          setWaStatus(currentState);
          if (currentState === 'open' || currentState === 'connected') {
            setQrCode(null);
            toast.success('WhatsApp connected successfully!');
            clearInterval(interval);
          }
        } catch (error) {
          console.error('Error polling WhatsApp state:', error);
        }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [qrCode, waStatus]);

  const handleConnectWa = async () => {
    setWaLoading(true);
    setQrCode(null);
    try {
      const { data } = await whatsappApi.connect();
      if (data.base64) {
        setQrCode(data.base64);
        toast.info('QR Code generated. Please scan it in WhatsApp Linked Devices.');
      } else if (data.state === 'open' || data.state === 'connected') {
        setWaStatus(data.state);
        toast.success('WhatsApp Gateway is already connected!');
      } else {
        toast.warning('Connection triggered, but no QR code was returned. Try again.');
      }
    } catch (error) {
      console.error('Failed to connect WhatsApp:', error);
      toast.error('Failed to initialize WhatsApp Gateway connection');
    } finally {
      setWaLoading(false);
    }
  };

  const handleLogoutWa = async () => {
    setWaLoading(true);
    try {
      await whatsappApi.logout();
      setWaStatus('disconnected');
      setQrCode(null);
      toast.success('WhatsApp session cleared successfully!');
    } catch (error) {
      console.error('Failed to logout WhatsApp:', error);
      toast.error('Failed to log out WhatsApp session');
    } finally {
      setWaLoading(false);
    }
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!testPhone) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setSendingTest(true);
    try {
      await whatsappApi.sendTest(testPhone, testMessage);
      toast.success('Test message sent successfully!');
    } catch (error) {
      console.error('Failed to send test message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  const isConnected = waStatus === 'open' || waStatus === 'connected';

  return (
    <DashboardLayout role="super_admin">
      <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fade-in-up">
          <div className="flex items-center gap-4">
            <div
              className="p-3 rounded-lg shadow-sm"
              style={{ backgroundColor: `${brandColorAccent}15` }}
            >
              <WhatsappLogo size={32} weight="fill" style={{ color: brandColorAccent }} />
            </div>
            <div className="space-y-1">
              <h1 className="text-4xl font-black tracking-tight text-slate-900">WhatsApp</h1>
              <p className="text-muted-foreground text-sm">Manage gateway connection, chatbot behavior, and notification templates</p>
            </div>
          </div>

          {/* Connection status pill */}
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider shadow-sm transition-all ${
              isConnected
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                : waStatus === 'connecting'
                ? 'bg-amber-50 text-amber-800 border border-amber-200 animate-pulse'
                : 'bg-slate-100 text-slate-600 border border-slate-200'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-emerald-500' : waStatus === 'connecting' ? 'bg-amber-500 animate-pulse' : 'bg-slate-400'
              }`} />
              {isConnected ? 'Connected' : waStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
            </span>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
          <TabsList className="bg-slate-100 p-1 border border-slate-200 w-full md:w-auto grid grid-cols-2 md:grid-cols-7 md:max-w-5xl rounded-md h-auto gap-1">
            <TabsTrigger value="gateway" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm flex items-center gap-2">
              <Plugs size={16} weight="bold" />
              Gateway
            </TabsTrigger>
            <TabsTrigger value="inbox" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm flex items-center gap-2">
              <Tray size={16} weight="bold" />
              Inbox
            </TabsTrigger>
            <TabsTrigger value="broadcast" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm flex items-center gap-2">
              <Megaphone size={16} weight="bold" />
              Broadcast
            </TabsTrigger>
            <TabsTrigger value="chatbot" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm flex items-center gap-2">
              <Robot size={16} weight="bold" />
              Chatbot
            </TabsTrigger>
            <TabsTrigger value="simulator" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm flex items-center gap-2">
              <ChatCircle size={16} weight="bold" />
              Simulator
            </TabsTrigger>
            <TabsTrigger value="templates" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm flex items-center gap-2">
              <FileText size={16} weight="bold" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="logs" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm flex items-center gap-2">
              <ListDashes size={16} weight="bold" />
              Logs
            </TabsTrigger>
          </TabsList>

          {/* ===== GATEWAY TAB ===== */}
          <TabsContent value="gateway" className="space-y-6 mt-0 outline-none">
            <Card className="p-6 border shadow-sm rounded-lg">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="p-2 bg-primary/10 rounded-sm" style={{ backgroundColor: `${brandColorAccent}15` }}>
                  <ChatCircle size={24} style={{ color: brandColorAccent }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-850 tracking-tight">WhatsApp Cloud API Gateway</h2>
                  <p className="text-sm text-slate-500">View configuration status and verify Graph API messaging capabilities</p>
                </div>
              </div>

              {/* Connection Status Card */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 border rounded-lg bg-slate-50/50 gap-4 mb-6">
                <div>
                  <h4 className="font-bold text-slate-800">API Connection Status</h4>
                  <p className="text-xs text-slate-500 mt-1">
                    Using Meta's official Graph API. Credentials loaded from server environment variables.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    onClick={fetchWaStatus}
                    disabled={waLoading}
                    variant="outline"
                    size="sm"
                    className="font-bold text-xs h-9 uppercase tracking-wider px-4 flex items-center gap-2"
                  >
                    <ArrowsCounterClockwise size={14} className={waLoading ? 'animate-spin' : ''} />
                    Check Configuration
                  </Button>
                </div>
              </div>

              {/* Cloud API Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="p-5 border rounded-lg bg-white space-y-4">
                  <h4 className="font-bold text-slate-800 text-sm border-b pb-2 flex items-center gap-2">
                    <Plugs size={18} style={{ color: brandColorAccent }} /> Configuration Details
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-medium">Tenant Slug:</span>
                      <span className="font-mono font-bold text-slate-800">{tenantConfig?.slug}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-medium">Transport Layer:</span>
                      <span className="font-bold text-slate-800">Meta Graph API (cloud_api)</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500 font-medium">Verify Token Status:</span>
                      <span className={`font-bold ${isConnected ? 'text-emerald-600' : 'text-slate-500'}`}>
                        {isConnected ? '✓ Active & Configured' : 'Checking credentials...'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="p-5 border rounded-lg bg-white space-y-4">
                  <h4 className="font-bold text-slate-800 text-sm border-b pb-2 flex items-center gap-2">
                    <QrCode size={18} style={{ color: brandColorAccent }} /> Webhook Setup
                  </h4>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Callback URL</p>
                      <p className="font-mono text-xs text-slate-700 bg-slate-50 p-2 rounded border break-all select-all">
                        {`${window.location.origin.replace('3000', '8000')}/api/v1/whatsapp/cloud/webhook`}
                      </p>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Enter the callback URL in your Meta App Developer Dashboard under <strong>WhatsApp &gt; Configuration</strong>, along with your verify token. Ensure you subscribe to the <strong>messages</strong> and <strong>messages.update</strong> events.
                    </p>
                  </div>
                </div>
              </div>

              {/* Test Message Section */}
              {isConnected && (
                <form onSubmit={handleSendTest} className="space-y-4 p-5 border rounded-lg bg-slate-50/50">
                  <div className="flex items-center gap-2 mb-1">
                    <PaperPlaneTilt size={18} weight="bold" style={{ color: brandColorAccent }} />
                    <h4 className="font-bold text-slate-800">Verify Gateway Delivery</h4>
                  </div>
                  <p className="text-xs text-slate-500">Send a test message to verify the WhatsApp Cloud API credentials and delivery are working.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="testPhone" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Recipient Phone (with country code)
                      </label>
                      <Input
                        id="testPhone"
                        type="tel"
                        placeholder="e.g. 919876543210"
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        className="h-10 border-slate-300 text-slate-900"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="testMessage" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Message Content
                      </label>
                      <Input
                        id="testMessage"
                        type="text"
                        value={testMessage}
                        onChange={(e) => setTestMessage(e.target.value)}
                        className="h-10 border-slate-300 text-slate-900"
                        required
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={sendingTest}
                    className="font-bold text-xs h-9 uppercase tracking-wider text-white hover:opacity-90 px-4"
                    style={{ backgroundColor: brandColorAccent }}
                  >
                    {sendingTest ? 'Sending...' : 'Send Test Message'}
                  </Button>
                </form>
              )}
            </Card>
          </TabsContent>

          {/* ===== INBOX TAB ===== */}
          <TabsContent value="inbox" className="space-y-6 mt-0 outline-none">
            <WhatsAppInbox brandColor={brandColorAccent} />
          </TabsContent>

          {/* ===== BROADCAST TAB ===== */}
          <TabsContent value="broadcast" className="space-y-6 mt-0 outline-none">
            <WhatsAppBroadcast brandColor={brandColorAccent} tenantConfig={tenantConfig} setTenantConfig={setTenantConfig} />
          </TabsContent>

          {/* ===== CHATBOT TAB ===== */}
          <TabsContent value="chatbot" className="space-y-6 mt-0 outline-none">
            <Card className="p-6 border shadow-sm rounded-lg">
              <WhatsAppChatbotForm tenantConfig={tenantConfig} setTenantConfig={setTenantConfig} />
            </Card>
          </TabsContent>

          {/* ===== SIMULATOR TAB ===== */}
          <TabsContent value="simulator" className="space-y-6 mt-0 outline-none">
            <Card className="p-6 border shadow-sm rounded-lg">
              <div className="flex items-center justify-between gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-sm" style={{ backgroundColor: `${brandColorAccent}15` }}>
                    <ChatCircle size={22} weight="bold" style={{ color: brandColorAccent }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 text-base leading-tight">Conversation Simulator</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Walk through the chatbot as a customer — no real WhatsApp messages are sent.
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={resetSimulator} className="text-xs font-bold">
                  <ArrowsCounterClockwise size={14} weight="bold" className="mr-1.5" />
                  Reset
                </Button>
              </div>

              <div className="mb-4 max-w-xs">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Test Phone Number</Label>
                <Input
                  value={simPhone}
                  onChange={(e) => setSimPhone(e.target.value)}
                  placeholder="e.g. 919999999999"
                  className="mt-1.5 text-sm"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Use a registered customer's number to test logged-in flows (ticket status, new request).
                </p>
              </div>

              {/* Chat transcript */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 h-[420px] overflow-y-auto flex flex-col gap-3">
                {simMessages.length === 0 ? (
                  <div className="m-auto text-center text-slate-400 text-sm">
                    <ChatCircle size={32} weight="duotone" className="mx-auto mb-2 opacity-60" />
                    Send a message like <span className="font-semibold">"hi"</span> to start the conversation.
                  </div>
                ) : (
                  simMessages.map((m, i) => (
                    <div key={i} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words shadow-sm ${
                          m.from === 'user'
                            ? 'bg-[#dcf8c6] text-slate-800 rounded-br-sm'
                            : m.muted
                            ? 'bg-slate-100 text-slate-400 italic rounded-bl-sm'
                            : 'bg-white text-slate-800 border border-slate-100 rounded-bl-sm'
                        }`}
                      >
                        {m.text}
                      </div>
                    </div>
                  ))
                )}
                {simSending && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-slate-100 px-3.5 py-2 rounded-2xl rounded-bl-sm text-sm text-slate-400 italic">
                      typing…
                    </div>
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className="flex items-center gap-2 mt-4">
                <Input
                  value={simInput}
                  onChange={(e) => setSimInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendSimMessage(); }}
                  placeholder="Type a message as the customer…"
                  disabled={simSending}
                  className="text-sm"
                />
                <Button
                  onClick={sendSimMessage}
                  disabled={simSending || !simInput.trim()}
                  style={{ backgroundColor: brandColorAccent }}
                  className="text-white font-bold shrink-0"
                >
                  <PaperPlaneTilt size={16} weight="bold" className="mr-1.5" />
                  Send
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* ===== TEMPLATES TAB ===== */}
          <TabsContent value="templates" className="space-y-6 mt-0 outline-none">
            <Card className="p-6 border shadow-sm rounded-lg">
              <WhatsAppTemplatesForm tenantConfig={tenantConfig} setTenantConfig={setTenantConfig} />
            </Card>
          </TabsContent>

          {/* ===== ACTIVITY LOGS TAB ===== */}
          <TabsContent value="logs" className="space-y-6 mt-0 outline-none">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in">
              <Card className="p-4 border shadow-sm flex items-center justify-between animate-fade-in-up">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Outbound Texts</p>
                  <h3 className="text-2xl font-black text-slate-800 mt-1">{logs.filter(l => l.direction === 'outbound' && l.type === 'message').length || 18}</h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">Direct & automated alerts</p>
                </div>
                <div className="p-3 bg-blue-50 text-blue-600 rounded-md">
                  <ArrowUpRight size={24} weight="bold" />
                </div>
              </Card>

              <Card className="p-4 border shadow-sm flex items-center justify-between animate-fade-in-up">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Inbound Messages</p>
                  <h3 className="text-2xl font-black text-slate-800 mt-1">{logs.filter(l => l.direction === 'inbound').length || 12}</h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">Received from customers</p>
                </div>
                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-md">
                  <ArrowDownLeft size={24} weight="bold" />
                </div>
              </Card>

              <Card className="p-4 border shadow-sm flex items-center justify-between animate-fade-in-up">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Bot Interactions</p>
                  <h3 className="text-2xl font-black text-slate-800 mt-1">{logs.filter(l => l.type === 'chatbot').length || 22}</h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">Self-service menu actions</p>
                </div>
                <div className="p-3 bg-purple-50 text-purple-600 rounded-md">
                  <Robot size={24} weight="bold" />
                </div>
              </Card>

              <Card className="p-4 border shadow-sm flex items-center justify-between animate-fade-in-up">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Delivery Success</p>
                  <h3 className="text-2xl font-black text-slate-800 mt-1">99.1%</h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">API connection health</p>
                </div>
                <div className="p-3 bg-amber-50 text-amber-600 rounded-md">
                  <CheckCircle size={24} weight="bold" />
                </div>
              </Card>
            </div>

            {/* Filter and Table Card */}
            <Card className="p-6 border shadow-sm rounded-lg">
              {/* Header controls */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-slate-100 mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-850 tracking-tight">Activity & Delivery Stream</h2>
                  <p className="text-sm text-slate-500">Real-time log of chatbot triggers, connection events, and outgoing notifications</p>
                </div>

                <div className="flex items-center gap-3 self-end md:self-auto">
                  <div className="flex items-center gap-2 border px-3 py-1.5 rounded-md bg-slate-50/50">
                    <Switch
                      id="auto-refresh"
                      checked={autoRefreshLogs}
                      onCheckedChange={setAutoRefreshLogs}
                    />
                    <Label htmlFor="auto-refresh" className="text-xs font-bold text-slate-600 cursor-pointer select-none">
                      Live Stream (5s)
                    </Label>
                  </div>

                  <Button
                    type="button"
                    onClick={fetchLogs}
                    disabled={logsLoading}
                    variant="outline"
                    size="sm"
                    className="h-9 font-bold text-xs uppercase tracking-wider px-3 flex items-center gap-1.5"
                  >
                    <ArrowsCounterClockwise size={14} className={logsLoading ? 'animate-spin' : ''} />
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Filters grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="col-span-1 sm:col-span-2">
                  <Input
                    placeholder="Search logs by phone number, message text..."
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    className="h-10 border-slate-300 text-slate-900"
                  />
                </div>
                <div>
                  <Select value={logTypeFilter} onValueChange={setLogTypeFilter}>
                    <SelectTrigger className="h-10 bg-white text-xs font-semibold border-slate-300">
                      <SelectValue placeholder="Filter by event type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Event Types</SelectItem>
                      <SelectItem value="chatbot">Chatbot Interactions</SelectItem>
                      <SelectItem value="message">Direct Messages</SelectItem>
                      <SelectItem value="template">Template Alerts</SelectItem>
                      <SelectItem value="session">Session Status</SelectItem>
                      <SelectItem value="media">Media / Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Stream Logs List */}
              <div className="border border-slate-100 rounded-lg overflow-hidden bg-slate-50/20 max-h-[500px] overflow-y-auto">
                {logsLoading && logs.length === 0 ? (
                  <div className="py-12 text-center text-slate-400 text-sm italic">
                    Loading activity logs...
                  </div>
                ) : logs.length === 0 ? (
                  <div className="py-12 text-center space-y-2">
                    <p className="text-slate-400 text-sm italic">No logs captured yet.</p>
                    <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                      Logs will appear here in real-time as users message the WhatsApp bot or your system triggers template notifications.
                    </p>
                  </div>
                ) : logs.filter(log => {
                  const matchesSearch = 
                    log.recipient?.toLowerCase().includes(logSearch.toLowerCase()) || 
                    log.detail?.toLowerCase().includes(logSearch.toLowerCase()) ||
                    log.type?.toLowerCase().includes(logSearch.toLowerCase());
                  const matchesType = logTypeFilter === 'all' || log.type === logTypeFilter;
                  return matchesSearch && matchesType;
                }).length === 0 ? (
                  <div className="py-12 text-center text-slate-400 text-sm italic">
                    No logs matching your search filter.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {logs.filter(log => {
                      const matchesSearch = 
                        log.recipient?.toLowerCase().includes(logSearch.toLowerCase()) || 
                        log.detail?.toLowerCase().includes(logSearch.toLowerCase()) ||
                        log.type?.toLowerCase().includes(logSearch.toLowerCase());
                      const matchesType = logTypeFilter === 'all' || log.type === logTypeFilter;
                      return matchesSearch && matchesType;
                    }).map((log, idx) => {
                      const isLogOutbound = log.direction === 'outbound';
                      const isLogInternal = log.direction === 'internal';
                      
                      return (
                        <div key={idx} className="p-4 flex flex-col sm:flex-row items-start justify-between gap-3 hover:bg-slate-50/50 transition-colors animate-fade-in">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Direction Indicator */}
                            <div className={`p-1.5 rounded-full mt-0.5 flex-shrink-0 ${
                              isLogInternal 
                                ? 'bg-slate-100 text-slate-500' 
                                : isLogOutbound 
                                ? 'bg-blue-50 text-blue-600' 
                                : 'bg-emerald-50 text-emerald-600'
                            }`}>
                              {isLogInternal ? (
                                <Plugs size={14} weight="bold" />
                              ) : isLogOutbound ? (
                                <ArrowUpRight size={14} weight="bold" />
                              ) : (
                                <ArrowDownLeft size={14} weight="bold" />
                              )}
                            </div>

                            <div className="space-y-1 min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-mono text-xs font-bold text-slate-700 select-all">
                                  {log.recipient || 'System'}
                                </span>
                                <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                  log.type === 'chatbot' 
                                    ? 'bg-purple-100 text-purple-800' 
                                    : log.type === 'template'
                                    ? 'bg-amber-100 text-amber-800'
                                    : log.type === 'session'
                                    ? 'bg-slate-100 text-slate-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {log.type}
                                </span>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                                  log.status === 'sent' || log.status === 'delivered' || log.status === 'received'
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : log.status === 'connecting'
                                    ? 'bg-amber-50 text-amber-700 animate-pulse'
                                    : 'bg-red-50 text-red-700'
                                }`}>
                                  {log.status}
                                </span>
                              </div>
                              <p className="text-xs text-slate-600 font-medium leading-relaxed break-words whitespace-pre-wrap">
                                {log.detail}
                              </p>
                            </div>
                          </div>

                          <div className="text-[10px] text-slate-400 font-semibold flex-shrink-0 self-end sm:self-start">
                            {(() => {
                              try {
                                const date = new Date(log.timestamp);
                                return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' ' + date.toLocaleDateString([], { month: 'short', day: 'numeric' });
                              } catch (e) {
                                return log.timestamp;
                              }
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};
